/**
 * Login/auth async thunks. Use authAPI for HTTP; dispatch existing action types.
 */
import { createAsyncThunk } from "@reduxjs/toolkit";
import {
  getCustomerConfig,
  getUserValidation,
  getIfsToken,
  logoutIfs,
} from "../../api/authAPI";
import { fetchIfsUserDetails, clearIfsUser } from "../ifsUser";
import { setAuthMessageBoxInfo } from "../authFlow/authFlowSlice";
import { logger } from "../../../utils/logger";
import CustomerConfig from "../../../azure/CustomerConfig";
import AuthTeams from "../../../auth/AuthTeams";
import {
  authService,
  SKIP_AUTH_DIALOG_AFTER_LOGOUT,
  SKIP_AUTH_DIALOG_AFTER_CANCEL,
} from "../../../services/authService";
import UserAuth from "../../../auth/userAuth";
import {
  ON_USER_DATA_LOAD,
  ON_AZURE_USER_LOAD,
  ON_IFS_USER_LOAD,
  ON_TOKEN_FAIL,
  ON_LOGIN_REFRESH_PREACTION,
  ON_LOGIN_REFRESH_PREACTION_INVOKE,
} from "../../constants/actionTypes";
import {
  USER_DATA_LOAD_STATUS,
  USER_TOKEN_STATUS,
  REFRESH_TOKEN_ACTION,
  RESPONSE_TYPE,
  DEFAULT_REFRESH_TOKEN_PRE_ACTION,
  ENV_NAME,
} from "../../constants/metaData";
import {
  ERROR_TYPE,
  ERROR_DETAILS,
  SERVER_RESPONSE_ERROR,
  NO_USER_ACCOUNT,
  NO_USER_ACCOUNT_ERROR,
  INVALID_CONFIGURATION,
  MISSING_TEAMS_CONTEXT,
  UNAUTHORIZED_USER,
  IFS_USER_ACCESS_DENIED,
  IFS_USER_AUTHENTICATION_FAILURE,
  FORBIDDEN,
  SERVER_ERROR,
  SERVER_RESPONSE_ISSUE,
  BAD_REQUEST_ERROR,
} from "../../constants/messageTypes";

const userAuth = new UserAuth();
/** Coalesce concurrent IFS refreshes (e.g. many API 401s + loginThunks). */
let ifsTokenRefreshSharedPromise = null;
/** Coalesce concurrent startup orchestrations (strict mode / repeated onStart). */
let runAuthFlowSharedPromise = null;

const APP_LOCAL_STORAGE_KEYS_TO_CLEAR = [
  "appInsightKey",
  "subscriptionKey",
  "applicationIDURI",
  "isOneDriveEnable",
  "environment",
  "dataLimit",
  "pageLimit",
  "ifsTokenEndPoint",
  "ifsClientId",
  "ifsRedirecturi",
  "isOneDriveDocDeleteEnable",
  "eTag",
  "ifsETag",
  "ifsUserCode",
  "userEmail",
  "ssoUserToken",
  "connectedEnvironmentLabel",
  "clientId",
  "tenantId",
  "userPrincipalName",
  "userId",
  "teamGroupId",
  "channelId",
  "channelMembershipType",
  "ownerGroupId",
  "groupId",
  "ifsUserDetails",
  "isOneDriveLoggedIn",
  "oneDriveExpiresAt",
  "oneDriveAccessToken",
  "oneDriveUserEmail",
  "oneDriveUserName",
];

function clearAppLocalStorageForLogout() {
  try {
    APP_LOCAL_STORAGE_KEYS_TO_CLEAR.forEach((key) =>
      localStorage.removeItem(key),
    );
    localStorage.setItem(SKIP_AUTH_DIALOG_AFTER_LOGOUT, "1");
    localStorage.removeItem(SKIP_AUTH_DIALOG_AFTER_CANCEL);
  } catch (_) {}
}

/** For client 401 handler: await this instead of dispatching another fetchIfsToken while refresh runs. */
export function getPendingIfsTokenRefreshPromise() {
  return ifsTokenRefreshSharedPromise;
}

/** Resolve config API env from REACT_APP_CONFIG_ENV (e.g. QA). Passed to getCustomerConfig(tenantId, upn, env). */
function getConfigEnv() {
  const raw = (process.env.REACT_APP_CONFIG_ENV || "").trim().toLowerCase();
  const env =
    {
      dev: ENV_NAME.dev,
      qa: ENV_NAME.qa,
      test: ENV_NAME.test,
      prod: ENV_NAME.prod,
    }[raw] || ENV_NAME.dev;
  logger.info("Config env selected", { env }, "REDUX");
  return env;
}

function rehydrateTeamsContextToLocalStorage(teamsContext) {
  if (!teamsContext || typeof localStorage === "undefined") return;

  if (process.env.REACT_APP_CLIENT_ID) {
    localStorage.setItem("clientId", process.env.REACT_APP_CLIENT_ID);
  }

  if (teamsContext?.user?.tenant?.id) {
    localStorage.setItem("tenantId", teamsContext.user.tenant.id);
  }
  const upn =
    teamsContext?.user?.userPrincipalName || teamsContext?.user?.loginHint || "";
  if (upn) {
    localStorage.setItem("userPrincipalName", upn);
  }
  if (teamsContext?.user?.id) {
    localStorage.setItem("userId", teamsContext.user.id);
  }
  if (teamsContext?.team?.groupId) {
    localStorage.setItem("teamGroupId", teamsContext.team.groupId);
  }
  if (teamsContext?.channel?.id) {
    localStorage.setItem("channelId", teamsContext.channel.id);
  }
  if (teamsContext?.channel?.displayName) {
    localStorage.setItem("isGeneralChannel", teamsContext.channel.displayName === "General");
  }
  if (teamsContext?.channel?.membershipType) {
    localStorage.setItem(
      "channelMembershipType",
      teamsContext.channel.membershipType,
    );
  }
  if (teamsContext?.channel?.ownerGroupId) {
    localStorage.setItem("ownerGroupId", teamsContext.channel.ownerGroupId);
  }
  const effectiveGroupId =
    teamsContext?.channel?.membershipType === "Private"
      ? teamsContext?.channel?.ownerGroupId
      : teamsContext?.team?.groupId;
  if (effectiveGroupId) {
    localStorage.setItem("groupId", effectiveGroupId);
  }
}

/** Startup: load customer config. Dispatches same actions as onStartUp. */
export const fetchCustomerConfig = createAsyncThunk(
  "login/fetchCustomerConfig",
  async (_, { dispatch, getState, rejectWithValue }) => {
    dispatch({
      type: ON_USER_DATA_LOAD,
      payload: USER_DATA_LOAD_STATUS.onLoading,
    });
    const teamsAuth = new AuthTeams();
    let tenantId = teamsAuth.getTenantId();
    let upn = teamsAuth.getUPN();
    let userId = teamsAuth.getUserId();
    const teamsContext = getState()?.Login?.teamsContext;

    // After logout we clear localStorage identity keys.
    // Retry/login should fall back to the in-memory Teams context captured at app startup.
    if (!tenantId || !upn) {
      rehydrateTeamsContextToLocalStorage(teamsContext);
      tenantId = tenantId || teamsContext?.user?.tenant?.id || "";
      upn =
        upn ||
        teamsContext?.user?.userPrincipalName ||
        teamsContext?.user?.loginHint ||
        "";
      userId = userId || teamsContext?.user?.id || "";
    }
    const env = getConfigEnv();
    try {
      const { data, status } = await getCustomerConfig(tenantId, upn, env);
      if (status === 200) {
        if (new CustomerConfig().setCustomerConfig(data) === false) {
          logger.error(
            "fetchCustomerConfig invalid configuration",
            null,
            "REDUX",
          );
          dispatch(
            setAuthMessageBoxInfo({
              type: ERROR_TYPE,
              message: INVALID_CONFIGURATION,
              errorMessage: "",
              error: null,
            }),
          );
          dispatch({
            type: ON_USER_DATA_LOAD,
            payload: USER_DATA_LOAD_STATUS.onConfigurationFailure,
          });
          return rejectWithValue("Invalid configuration");
        }
        dispatch({
          type: ON_USER_DATA_LOAD,
          payload: USER_DATA_LOAD_STATUS.onIFSUserValidation,
        });
        logger.info("fetchCustomerConfig success", { status }, "REDUX");
        return data;
      }
      logger.error("fetchCustomerConfig non-200", { status }, "REDUX");
      dispatch(
        setAuthMessageBoxInfo({
          type: ERROR_TYPE,
          message: NO_USER_ACCOUNT,
          errorMessage: `Error Code: ${status}: ${NO_USER_ACCOUNT_ERROR}`,
          error: null,
        }),
      );
      dispatch({
        type: ON_USER_DATA_LOAD,
        payload: USER_DATA_LOAD_STATUS.onConfigurationFailure,
      });
      return rejectWithValue({ message: `Status ${status}` });
    } catch (err) {
      logger.error("fetchCustomerConfig failed", err, "REDUX");
      const status = err?.response?.status;
      let message = SERVER_ERROR;
      let errorMessage = err?.message ?? "";
      if (err?.code === "MISSING_TENANT_UPN") {
        message = MISSING_TEAMS_CONTEXT;
        errorMessage = err.message;
      } else if (status === 401) message = UNAUTHORIZED_USER;
      else if (status === 403) message = FORBIDDEN;
      else if (status === 400 || status === 404) message = SERVER_ERROR;
      else message = SERVER_RESPONSE_ISSUE;
      if (errorMessage === "" && err?.response?.data != null) {
        const d = err.response.data;
        errorMessage = typeof d === "string" ? d : "";
      }
      if (!errorMessage)
        errorMessage = `Error Code: ${status ?? ""}: ${SERVER_RESPONSE_ERROR}`;
      dispatch(
        setAuthMessageBoxInfo({
          type: ERROR_TYPE,
          message,
          errorMessage,
          error: err,
        }),
      );
      dispatch({
        type: ON_USER_DATA_LOAD,
        payload: USER_DATA_LOAD_STATUS.onConfigurationFailure,
      });
      return rejectWithValue({ message: err?.message ?? "Unknown error" });
    }
  },
);

/** Validate IFS user. Dispatches same actions as onIfsUserValidate. */
export const fetchUserValidation = createAsyncThunk(
  "login/fetchUserValidation",
  async (_, { dispatch, rejectWithValue }) => {
    logger.info("fetchUserValidation start", null, "REDUX");
    try {
      const { data, status } = await getUserValidation();
      if (status === 200) {
        dispatch({
          type: ON_USER_DATA_LOAD,
          payload: USER_DATA_LOAD_STATUS.onAzureUserAuthentication,
        });
        dispatch({
          type: ON_IFS_USER_LOAD,
          payload: USER_TOKEN_STATUS.authorized,
        });
        dispatch(fetchIfsUserDetails());
        logger.info("fetchUserValidation success", { status }, "REDUX");
        return data;
      }
      logger.error("fetchUserValidation non-200", { status }, "REDUX");
      const errorMessage =
        data !== undefined && data !== null
          ? typeof data === "string"
            ? data
            : ""
          : "";
      dispatch(
        setAuthMessageBoxInfo({
          type: ERROR_TYPE,
          message: NO_USER_ACCOUNT,
          errorMessage: errorMessage || `Error Code: ${status}`,
          error: null,
        }),
      );
      dispatch({
        type: ON_USER_DATA_LOAD,
        payload: USER_DATA_LOAD_STATUS.onIFSUserLogin,
      });
      dispatch({
        type: ON_IFS_USER_LOAD,
        payload: USER_TOKEN_STATUS.invalid,
      });
      return rejectWithValue(new Error(`Status ${status}`));
    } catch (err) {
      const status = err?.response?.status;
      let errorMessage = "";
      if (err?.response?.data != null) {
        const d = err.response.data;
        errorMessage = typeof d === "string" ? d : "";
      }
      const messageBox = {
        type: ERROR_TYPE,
        message: SERVER_ERROR,
        errorMessage:
          errorMessage || `${ERROR_DETAILS} ${SERVER_RESPONSE_ERROR}`,
        error: err,
      };
      if (status === 401 || status === 403) {
        messageBox.message = IFS_USER_ACCESS_DENIED;
        messageBox.errorMessage =
          errorMessage ||
          `Error Code: ${status}: ${IFS_USER_AUTHENTICATION_FAILURE}`;
        dispatch({
          type: ON_USER_DATA_LOAD,
          payload:
            status === 401
              ? USER_DATA_LOAD_STATUS.onIFSUserLogin
              : USER_DATA_LOAD_STATUS.onIfsUserAuthenticationFailure,
        });
        dispatch({
          type: ON_IFS_USER_LOAD,
          payload:
            status === 401
              ? USER_TOKEN_STATUS.expired
              : USER_TOKEN_STATUS.invalid,
        });
      } else {
        dispatch(setAuthMessageBoxInfo(messageBox));
        dispatch({
          type: ON_USER_DATA_LOAD,
          payload: USER_DATA_LOAD_STATUS.onIfsUserAuthenticationFailure,
        });
        dispatch({
          type: ON_IFS_USER_LOAD,
          payload: USER_TOKEN_STATUS.invalid,
        });
      }
      return rejectWithValue({ message: err?.message ?? "Unknown error" });
    }
  },
);

/** Azure token request. Dispatches same actions as onAzureTokenRequest. */
export const fetchAzureToken = createAsyncThunk(
  "login/fetchAzureToken",
  async (refreshToken = true, { dispatch, rejectWithValue }) => {
    logger.info("fetchAzureToken start", { refreshToken }, "REDUX");
    const tokenResponse = await userAuth.getUserToken(refreshToken);
    if (tokenResponse && tokenResponse.responseCode === 200) {
      if (!refreshToken) {
        dispatch({
          type: ON_USER_DATA_LOAD,
          payload: USER_DATA_LOAD_STATUS.onCompletion,
        });
      }
      dispatch({
        type: ON_AZURE_USER_LOAD,
        payload: USER_TOKEN_STATUS.authorized,
      });
      logger.info("fetchAzureToken success", null, "REDUX");
      return tokenResponse;
    }
    logger.error("fetchAzureToken token fail", tokenResponse, "REDUX");
    dispatch({
      type: ON_TOKEN_FAIL,
      payload: tokenResponse,
    });
    return rejectWithValue("Token fail");
  },
);

/**
 * Single auth orchestrator (IFS cache-first -> IFS code exchange if needed -> Azure).
 * This replaces multi-step orchestration done by `MainLayout`.
 */
export const runAuthFlow = createAsyncThunk(
  "login/runAuthFlow",
  async (_, { dispatch, rejectWithValue }) => {
    if (runAuthFlowSharedPromise) {
      logger.info(
        "runAuthFlow: awaiting in-flight startup flow",
        null,
        "REDUX",
      );
      return runAuthFlowSharedPromise;
    }
    runAuthFlowSharedPromise = (async () => {
      try {
        // 1) Load customer config (Customer-Key, endpoints, redirect uri, etc.)
        await dispatch(fetchCustomerConfig()).unwrap();

        const hasIfsUserKey = !!new AuthTeams().getUserKey();

        // 2) Cache-first only when a previous IFS auth code exists.
        // Without User-Key, get-uservalidation fails with "User-Key missing in header."
        if (hasIfsUserKey) {
          try {
            await dispatch(fetchUserValidation()).unwrap();
          } catch (_) {
            // 3) If cache validation fails -> do IFS login (authorization_code exchange) then cache it.
            await dispatch(fetchIfsToken({ refreshToken: false })).unwrap();
          }
        } else {
          logger.info(
            "runAuthFlow: no cached IFS User-Key, starting interactive IFS login",
            null,
            "REDUX",
          );
          await dispatch(fetchIfsToken({ refreshToken: false })).unwrap();
        }

        // 4) Now request Azure/MS Graph token
        await dispatch(fetchAzureToken(false)).unwrap();
        return true;
      } catch (err) {
        return rejectWithValue(err?.message ?? "Auth flow failed");
      }
    })().finally(() => {
      runAuthFlowSharedPromise = null;
    });
    return runAuthFlowSharedPromise;
  },
);

/** IFS user login (auth code + get-ifs-token). Uses authService.startLogin() then authAPI.getIfsToken. */
export const fetchIfsToken = createAsyncThunk(
  "login/fetchIfsToken",
  async (
    {
      refreshToken = true,
      silent = false,
      preAction = {
        retry: 0,
        actionType: REFRESH_TOKEN_ACTION.none,
        refreshResponse: RESPONSE_TYPE.none,
      },
    },
    { dispatch, rejectWithValue },
  ) => {
    if (ifsTokenRefreshSharedPromise) {
      logger.info("fetchIfsToken: awaiting in-flight refresh", null, "REDUX");
      return ifsTokenRefreshSharedPromise;
    }
    ifsTokenRefreshSharedPromise = (async () => {
      logger.info("fetchIfsToken start", { silent }, "REDUX");
      const failedPreAction = {
        retry: preAction.retry,
        actionType: preAction.actionType,
        refreshResponse: RESPONSE_TYPE.failure,
      };
      try {
        dispatch({
          type: ON_LOGIN_REFRESH_PREACTION_INVOKE,
          payload: {
            retry: 0,
            actionType: REFRESH_TOKEN_ACTION.none,
            refreshResponse: RESPONSE_TYPE.none,
          },
        });
        const ifsAuthCodeData = await authService.startLogin({
          silentOnly: silent,
        });
        if (ifsAuthCodeData.status !== 200) {
          logger.error(
            "fetchIfsToken startLogin non-200",
            { status: ifsAuthCodeData.status },
            "REDUX",
          );
          const errorMessage =
            ifsAuthCodeData.error != null && ifsAuthCodeData.error !== ""
              ? ifsAuthCodeData.error
              : IFS_USER_AUTHENTICATION_FAILURE;
          dispatch(
            setAuthMessageBoxInfo({
              type: ERROR_TYPE,
              message: IFS_USER_ACCESS_DENIED,
              errorMessage,
              error: errorMessage,
            }),
          );
          dispatch({
            type: ON_USER_DATA_LOAD,
            payload: USER_DATA_LOAD_STATUS.onIfsUnauthorizedUser,
          });
          dispatch({
            type: ON_IFS_USER_LOAD,
            payload: USER_TOKEN_STATUS.invalid,
          });
          dispatch({
            type: ON_LOGIN_REFRESH_PREACTION_INVOKE,
            payload: failedPreAction,
          });
          dispatch({
            type: ON_LOGIN_REFRESH_PREACTION,
            payload: DEFAULT_REFRESH_TOKEN_PRE_ACTION,
          });
          return rejectWithValue({ message: errorMessage });
        }
        const { data, status } = await getIfsToken(ifsAuthCodeData.data);
        if (status === 200) {
          dispatch({
            type: ON_IFS_USER_LOAD,
            payload: USER_TOKEN_STATUS.authorized,
          });
          dispatch(fetchIfsUserDetails());
          if (!refreshToken) {
            dispatch({
              type: ON_USER_DATA_LOAD,
              payload: USER_DATA_LOAD_STATUS.onAzureUserAuthentication,
            });
          } else {
            dispatch({
              type: ON_LOGIN_REFRESH_PREACTION_INVOKE,
              payload: {
                retry: preAction.retry,
                actionType: preAction.actionType,
                refreshResponse: RESPONSE_TYPE.success,
              },
            });
          }
          logger.info("fetchIfsToken success", { status }, "REDUX");
          return data;
        }
        logger.error("fetchIfsToken non-200/204", { status }, "REDUX");
        if (status === 204) {
          dispatch(
            setAuthMessageBoxInfo({
              type: ERROR_TYPE,
              message: NO_USER_ACCOUNT,
              errorMessage: `Error Code: ${status}: ${NO_USER_ACCOUNT_ERROR}`,
              error: null,
            }),
          );
          dispatch({
            type: ON_USER_DATA_LOAD,
            payload: USER_DATA_LOAD_STATUS.onIfsUnauthorizedUser,
          });
          dispatch({
            type: ON_IFS_USER_LOAD,
            payload: USER_TOKEN_STATUS.invalid,
          });
          dispatch({
            type: ON_LOGIN_REFRESH_PREACTION,
            payload: { retry: 0, actionType: REFRESH_TOKEN_ACTION.none },
          });
          dispatch({
            type: ON_LOGIN_REFRESH_PREACTION_INVOKE,
            payload: failedPreAction,
          });
          return rejectWithValue({ message: `Status ${status}` });
        }
        const errorMessage =
          data !== undefined && data !== null
            ? typeof data === "string"
              ? data
              : ""
            : "";
        dispatch({
          type: ON_USER_DATA_LOAD,
          payload: USER_DATA_LOAD_STATUS.onIfsUnauthorizedUser,
        });
        // Note: auth error message is handled via AuthFlow message box state.
        dispatch(
          setAuthMessageBoxInfo({
            type: ERROR_TYPE,
            message: NO_USER_ACCOUNT,
            errorMessage: errorMessage || `Error Code: ${status}`,
            error: null,
          }),
        );
        dispatch({
          type: ON_IFS_USER_LOAD,
          payload: USER_TOKEN_STATUS.invalid,
        });
        dispatch({
          type: ON_LOGIN_REFRESH_PREACTION,
          payload: { retry: 0, actionType: REFRESH_TOKEN_ACTION.none },
        });
        dispatch({
          type: ON_LOGIN_REFRESH_PREACTION_INVOKE,
          payload: failedPreAction,
        });
        return rejectWithValue({ message: `Status ${status}` });
      } catch (err) {
        logger.error("fetchIfsToken failed", err, "REDUX");
        const status = err?.response?.status;
        let errorMessage = err?.message ?? "";
        if (err?.response?.data != null) {
          const d = err.response.data;
          errorMessage = typeof d === "string" ? d : String(d);
        }
        let message = SERVER_ERROR;
        if (status === 401) message = IFS_USER_ACCESS_DENIED;
        else if (status === 403) message = FORBIDDEN;
        else if (status === 400) message = BAD_REQUEST_ERROR;
        else if (status === 404) message = NO_USER_ACCOUNT;
        else message = SERVER_RESPONSE_ISSUE;
        dispatch(
          setAuthMessageBoxInfo({
            type: ERROR_TYPE,
            message,
            errorMessage:
              errorMessage || `${ERROR_DETAILS} ${SERVER_RESPONSE_ERROR}`,
            error: null,
          }),
        );
        dispatch({
          type: ON_USER_DATA_LOAD,
          payload: USER_DATA_LOAD_STATUS.onIfsUnauthorizedUser,
        });
        dispatch({
          type: ON_IFS_USER_LOAD,
          payload: USER_TOKEN_STATUS.invalid,
        });
        dispatch({
          type: ON_LOGIN_REFRESH_PREACTION,
          payload: { retry: 0, actionType: REFRESH_TOKEN_ACTION.none },
        });
        dispatch({
          type: ON_LOGIN_REFRESH_PREACTION_INVOKE,
          payload: failedPreAction,
        });
        return rejectWithValue({
          message: errorMessage || err?.message || "Unknown error",
        });
      }
    })().finally(() => {
      ifsTokenRefreshSharedPromise = null;
    });
    return ifsTokenRefreshSharedPromise;
  },
);

/**
 * Single retry/Login flow: set loading state and run the right auth step.
 * Replaces multiple dispatchers in the UI; use from useLogin().onRetry(dataLoadStatus).
 */
export const retryLogin = createAsyncThunk(
  "login/retryLogin",
  async (_dataLoadStatus, { dispatch, rejectWithValue }) => {
    let shouldForceInteractiveLogin = false;
    try {
      shouldForceInteractiveLogin = Boolean(
        localStorage.getItem(SKIP_AUTH_DIALOG_AFTER_LOGOUT) ||
        localStorage.getItem(SKIP_AUTH_DIALOG_AFTER_CANCEL),
      );
      localStorage.removeItem(SKIP_AUTH_DIALOG_AFTER_LOGOUT);
      localStorage.removeItem(SKIP_AUTH_DIALOG_AFTER_CANCEL);
    } catch (_) {}

    try {
      // Explicit user click after cancel/logout should open auth dialog again.
      // Bypass cache-first validation once so startLogin is invoked.
      if (shouldForceInteractiveLogin) {
        await dispatch(fetchCustomerConfig()).unwrap();
        await dispatch(fetchIfsToken({ refreshToken: false })).unwrap();
        await dispatch(fetchAzureToken(false)).unwrap();
      } else {
        // Default path keeps centralized cache-first orchestration.
        await dispatch(runAuthFlow()).unwrap();
      }
      return true;
    } catch (err) {
      return rejectWithValue(err?.message ?? "Retry login failed");
    }
  },
);

/**
 * Logout: call backend /api/logout then reset auth-related UI state.
 * Integrates with the existing Redux-driven flow in MainLayout (userDataLoadStatus gates the LoginPage).
 */
export const logoutUser = createAsyncThunk(
  "login/logoutUser",
  async (_, { dispatch }) => {
    logger.info("logoutUser start", null, "REDUX");
    try {
      await logoutIfs();
    } catch (err) {
      // Continue local logout even if backend call fails.
      logger.warn(
        "logoutUser: logoutIfs API failed, continuing with local logout",
        err,
        "REDUX",
      );
    } finally {
      // Clear local IFS identity so the next validation triggers login flow.
      clearAppLocalStorageForLogout();
      localStorage.removeItem("ifsUserCode");
      localStorage.removeItem("userEmail");
      // Prevent auth dialog from auto-opening after logout; cleared when user clicks Retry.
      localStorage.setItem(SKIP_AUTH_DIALOG_AFTER_LOGOUT, "1");
    }

    // Clear IFS user details (Redux + localStorage).
    dispatch(clearIfsUser());
    // Reset auth state so MainLayout immediately shows LoginPage and restarts startup flow.
    dispatch({ type: ON_IFS_USER_LOAD, payload: USER_TOKEN_STATUS.invalid });
    dispatch({ type: ON_AZURE_USER_LOAD, payload: USER_TOKEN_STATUS.invalid });
    dispatch({
      type: ON_USER_DATA_LOAD,
      payload: USER_DATA_LOAD_STATUS.onStart,
    });

    logger.info("logoutUser completed", null, "REDUX");
    return true;
  },
);
